import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Grid,
  Stack,
  Typography,
  LinearProgress,
  Divider,
  List,
  Chip,
  ListItem,
  ListItemText,
  Paper
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import InsightsIcon from '@mui/icons-material/Insights';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import StarRateIcon from '@mui/icons-material/StarRate';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FolderIcon from '@mui/icons-material/Folder';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useDispatch } from 'react-redux';
import { getSummaryList } from '../../store/Slice/Payout/PayoutSlice';
import usePayoutSelector from '../../store/Slice/Payout/PayoutSelector';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { fDate } from '../../utils/formatTime';
import { normalizeValue } from '../../utils/CommonFunction';
import Label from '../../components/Label';

const BANK_PRIMARY = '#13519d';

function KpiCard({ icon, label, value, color = BANK_PRIMARY }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        bgcolor: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 6px 24px rgba(0,0,0,0.08)'
        }
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '12px',
          bgcolor: `${color}14`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color,
          flexShrink: 0
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '1.25rem',
            color: '#1e293b',
            lineHeight: 1.2
          }}
        >
          {value || '0'}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: '#64748b',
            fontWeight: 600,
            mt: 0.5,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}
        >
          {label}
        </Typography>
      </Box>
    </Paper>
  );
}

function Summary() {
  const [targets] = useState({
    monthly: { target: 5000000, achieved: 3500000 },
    quarterly: { target: 15000000, achieved: 10500000 },
    yearly: { target: 60000000, achieved: 42000000 }
  });

  const { LoginData } = useLoginSelector();
  const { PayoutSummaryData } = usePayoutSelector();
  const dispatch = useDispatch();

  const advanceRequests = PayoutSummaryData?.advances;

  const pct = (achieved, target) => {
    if (target === 0) return 0;
    return Math.round((achieved / target) * 100);
  };

  useEffect(() => {
    if (LoginData?.userId) {
      dispatch(getSummaryList({ userId: LoginData.userId }));
    }
  }, [LoginData?.userId, dispatch]);

  const summaryCards = [
    LoginData?.userType !== 'ADMIN' && {
      label: 'Total Amount Earned',
      value: PayoutSummaryData?.accountOverview?.commissionEarned
        ? `₹${PayoutSummaryData.accountOverview.commissionEarned}`
        : '₹0',
      icon: <CurrencyRupeeIcon sx={{ fontSize: 26 }} />,
      color: '#13519d'
    },
    {
      label: 'Avg Monthly Business',
      value: PayoutSummaryData?.summary?.averageMonthlyBusiness || '₹0',
      icon: <AssessmentIcon sx={{ fontSize: 26 }} />,
      color: '#0284c7'
    },
    {
      label: 'Total Files',
      value: PayoutSummaryData?.summary?.totalFiles || '0',
      icon: <FolderIcon sx={{ fontSize: 26 }} />,
      color: '#16a34a'
    },
    {
      label: 'Success Ratio',
      value: `${PayoutSummaryData?.summary?.successRatio || 0}%`,
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 26 }} />,
      color: '#8b5cf6'
    }
  ].filter(Boolean);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Top KPI Metric Cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {summaryCards.map((card, i) => (
          <Grid item xs={12} sm={6} md={12 / summaryCards.length} key={i}>
            <KpiCard icon={card.icon} label={card.label} value={card.value} color={card.color} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* FCR Overview */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              p: 3,
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  bgcolor: 'rgba(19, 81, 157, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#13519d'
                }}
              >
                <WorkspacePremiumIcon fontSize="small" />
              </Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                FCR Overview
              </Typography>
            </Stack>

            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    borderColor: '#e2e8f0',
                    bgcolor: '#fafbfc'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        bgcolor: 'rgba(245, 158, 11, 0.12)',
                        color: '#d97706',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <WorkspacePremiumIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: '#64748b',
                          textTransform: 'uppercase'
                        }}
                      >
                        Partner Category
                      </Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1e293b', mt: 0.2 }}>
                        Gold
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    borderColor: '#e2e8f0',
                    bgcolor: '#fafbfc'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        bgcolor: 'rgba(234, 179, 8, 0.12)',
                        color: '#ca8a04',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <StarRateIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: '#64748b',
                          textTransform: 'uppercase'
                        }}
                      >
                        Partner Rating
                      </Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1e293b', mt: 0.2 }}>
                        4.8 / 5.0
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    borderColor: '#e2e8f0',
                    bgcolor: '#fafbfc'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        bgcolor: 'rgba(19, 81, 157, 0.08)',
                        color: '#13519d',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <LeaderboardIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: '#64748b',
                          textTransform: 'uppercase'
                        }}
                      >
                        Partner Rank
                      </Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1e293b', mt: 0.2 }}>
                        #12
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    borderColor: '#e2e8f0',
                    bgcolor: '#fafbfc'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        bgcolor: 'rgba(22, 163, 74, 0.12)',
                        color: '#16a34a',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <CreditScoreIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: '#64748b',
                          textTransform: 'uppercase'
                        }}
                      >
                        Partner Credit Limit
                      </Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1e293b', mt: 0.2 }}>
                        ₹1,000,000
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* Target vs Achievement */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              p: 3,
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  bgcolor: 'rgba(19, 81, 157, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#13519d'
                }}
              >
                <TrendingUpIcon fontSize="small" />
              </Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                Target vs Achievement
              </Typography>
            </Stack>

            <Stack spacing={3}>
              <Box>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                      Monthly Target
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                      Active month target & achievement
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#13519d' }}>
                      ₹{targets.monthly.achieved.toLocaleString('en-IN')} / ₹
                      {targets.monthly.target.toLocaleString('en-IN')}
                    </Typography>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#16a34a' }}>
                      {pct(targets.monthly.achieved, targets.monthly.target)}% Completed
                    </Typography>
                  </Box>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={pct(targets.monthly.achieved, targets.monthly.target)}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: '#e2e8f0',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: '#13519d',
                      borderRadius: 4
                    }
                  }}
                />
              </Box>

              <Box>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                      Quarterly Target
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                      Current quarter progress
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#13519d' }}>
                      ₹{targets.quarterly.achieved.toLocaleString('en-IN')} / ₹
                      {targets.quarterly.target.toLocaleString('en-IN')}
                    </Typography>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#16a34a' }}>
                      {pct(targets.quarterly.achieved, targets.quarterly.target)}% Completed
                    </Typography>
                  </Box>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={pct(targets.quarterly.achieved, targets.quarterly.target)}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: '#e2e8f0',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: '#0284c7',
                      borderRadius: 4
                    }
                  }}
                />
              </Box>
            </Stack>
          </Card>
        </Grid>

        {/* Advance List & Credit Summary */}
        <Grid item xs={12}>
          <Card
            sx={{
              p: 3,
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  bgcolor: 'rgba(19, 81, 157, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#13519d'
                }}
              >
                <AccountBalanceWalletIcon fontSize="small" />
              </Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                Advance & Credit Summary
              </Typography>
            </Stack>

            <Grid container spacing={2.5} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    borderColor: '#e2e8f0',
                    bgcolor: '#fafbfc'
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#64748b',
                      textTransform: 'uppercase'
                    }}
                  >
                    Credit Limit
                  </Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#1e293b', mt: 0.5 }}>
                    {normalizeValue(PayoutSummaryData?.creditSummary?.creditLimit, '—')}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    borderColor: '#e2e8f0',
                    bgcolor: '#fafbfc'
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#64748b',
                      textTransform: 'uppercase'
                    }}
                  >
                    Used
                  </Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#d97706', mt: 0.5 }}>
                    {normalizeValue(PayoutSummaryData?.creditSummary?.used, '—')}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    borderColor: '#e2e8f0',
                    bgcolor: '#fafbfc'
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#64748b',
                      textTransform: 'uppercase'
                    }}
                  >
                    Available
                  </Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#16a34a', mt: 0.5 }}>
                    {normalizeValue(PayoutSummaryData?.creditSummary?.available, '—')}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2.5, borderColor: '#e2e8f0' }} />

            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1e293b', mb: 1.5 }}>
              Recent Advance Requests
            </Typography>

            <List dense sx={{ p: 0 }}>
              {(!advanceRequests || advanceRequests.length === 0) && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="No advance requests found"
                    secondary="No requests have been submitted yet."
                    primaryTypographyProps={{ fontSize: 13, fontWeight: 600, color: '#64748b' }}
                  />
                </ListItem>
              )}
              {advanceRequests &&
                advanceRequests.slice(0, 3).map((r) => (
                  <ListItem
                    key={r.partnerAdvanceId}
                    sx={{
                      px: 2,
                      py: 1.5,
                      mb: 1,
                      borderRadius: '10px',
                      border: '1px solid #e2e8f0',
                      bgcolor: '#fafbfc'
                    }}
                    secondaryAction={
                      <Label
                        variant="ghost"
                        color={
                          r.approvalStatus === 'APPROVED'
                            ? 'success'
                            : r.approvalStatus === 'REJECTED'
                            ? 'error'
                            : 'warning'
                        }
                      >
                        {r.approvalStatus}
                      </Label>
                    }
                  >
                    <ListItemText
                      primary={`ADV-${r.partnerAdvanceId} • ₹${Number(
                        r.requestAmount || 0
                      ).toLocaleString('en-IN')}`}
                      secondary={`${fDate(r.requestDate)} • ${r.purpose || 'Advance Request'}`}
                      primaryTypographyProps={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}
                      secondaryTypographyProps={{ fontSize: 12, color: '#64748b', mt: 0.3 }}
                    />
                  </ListItem>
                ))}
            </List>

            <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 2 }}>
              Note: Advances are subject to credit policy and verification. Pending requests will be
              reviewed by the credit team.
            </Typography>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Summary;
